import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const SCROLL_IDLE_DELAY = 140;
const MIN_SCROLL_DELTA = 2;

function NavButton({ icon, label, active, onClick, terminal = false }: { icon: string; label: string; active?: boolean; onClick?: () => void; terminal?: boolean }) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={terminal ? `cyber-nav ${active ? 'active' : ''}` : `flex flex-col items-center gap-0.5 transition-colors min-w-[48px] ${
        active ? 'text-[var(--accent)]' : 'text-[var(--text-muted)] hover:text-[var(--text)]'
      }`}
    >
      <span aria-hidden="true">{terminal ? <b>{icon}</b> : <span className="text-lg">{icon}</span>}</span>
      <span className={terminal ? undefined : 'text-[10px] font-medium'}>{label}</span>
    </button>
  );
}

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const terminal = path === '/';
  const [isScrolling, setIsScrolling] = useState(false);
  const lastScrollYRef = useRef(0);
  const scrollIdleTimerRef = useRef<number | null>(null);

  // Hide on game pages and level pages
  const hiddenPaths = ['/game/', '/level/'];
  const shouldHideByRoute = hiddenPaths.some(p => path.startsWith(p));

  useEffect(() => {
    const clearIdleTimer = () => {
      if (scrollIdleTimerRef.current) {
        window.clearTimeout(scrollIdleTimerRef.current);
        scrollIdleTimerRef.current = null;
      }
    };

    lastScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = Math.abs(currentScrollY - lastScrollYRef.current);

      if (delta < MIN_SCROLL_DELTA) {
        return;
      }

      lastScrollYRef.current = currentScrollY;
      setIsScrolling(true);

      clearIdleTimer();
      scrollIdleTimerRef.current = window.setTimeout(() => {
        setIsScrolling(false);
        scrollIdleTimerRef.current = null;
      }, SCROLL_IDLE_DELAY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearIdleTimer();
    };
  }, []);

  useEffect(() => {
    setIsScrolling(false);
  }, [path]);

  if (shouldHideByRoute) {
    return null;
  }

  return (
    <nav
      className={`${terminal ? 'cyber-bottom' : 'fixed bottom-0 left-0 right-0 z-40 border-t border-[var(--border)] bg-[color:var(--surface)]/92 shadow-[var(--shadow-soft)] backdrop-blur-lg transition-all duration-200 mx-auto sm:max-w-md md:max-w-lg sm:border-x'} ${
        isScrolling ? 'translate-y-[120%] opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
      }`}
    >
      <div className={terminal ? undefined : "mx-auto flex max-w-lg items-center justify-around px-4 py-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))]"}>
        <NavButton terminal={terminal} icon={terminal ? '⌂' : '🏠'} label="首页" active={path === '/'} onClick={() => navigate('/')} />
        <NavButton terminal={terminal} icon={terminal ? '♛' : '🏆'} label="成就" active={path === '/achievements'} onClick={() => navigate('/achievements')} />
        <NavButton terminal={terminal} icon={terminal ? '◈' : '🏅'} label="排行" active={path === '/leaderboard'} onClick={() => navigate('/leaderboard')} />
        <NavButton terminal={terminal} icon={terminal ? '▣' : '🛒'} label="商店" active={path === '/shop'} onClick={() => navigate('/shop')} />
        <NavButton terminal={terminal} icon={terminal ? '♙' : '👤'} label="我的" active={path.startsWith('/parent')} onClick={() => navigate('/parent')} />
      </div>
    </nav>
  );
}
